import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useImportCompose } from "../../api/queries";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface ImportComposeModalProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
  /**
   * Optional prefill for the textarea, seeded when the dialog opens (M2
   * PR-4, #245 — the Gallery's "Deploy this stack" seam: see
   * GalleryPage.tsx / ProjectDetailPage.tsx). Omitted (or `undefined`) by
   * every other caller, which preserves the original behavior exactly: the
   * textarea starts empty. Never a secret VALUE — a recipe's compose only
   * ever carries `${secret:NAME}` references (S-12/S-13).
   */
  initialCompose?: string;
}

const ACCEPTED_FILE_EXTENSIONS = ".yml,.yaml";

/**
 * Imports a pasted-or-picked compose.yml into an EMPTY project (ADR-0025 §2:
 * compose is an import/export format, never the model this UI edits
 * directly). Creates one service per compose service and one connection per
 * inferred edge — never a placement (the operator places afterward).
 *
 * Requires an empty project — the server refuses 409 otherwise, rendered
 * here as a plain-language note rather than an alarming failure (Q-7);
 * merging an import into an existing graph is a future feature, not
 * supported yet.
 */
export function ImportComposeModal({ open, projectId, onClose, initialCompose }: ImportComposeModalProps) {
  const textareaId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { pushToast } = useToast();
  const mutation = useImportCompose(projectId);

  const [composeText, setComposeText] = useState(initialCompose ?? "");
  const [fileError, setFileError] = useState<string | null>(null);

  // Re-seeds the textarea every time the dialog OPENS (never while it's
  // already open and the operator is mid-edit) — matches `handleClose`
  // below, which already always resets to "" on the way out, so this is the
  // symmetric "reset to the starting point" on the way in. With no
  // `initialCompose` passed (every caller before M2 PR-4), `initialCompose
  // ?? ""` is exactly the original always-empty behavior.
  useEffect(() => {
    if (open) {
      setComposeText(initialCompose ?? "");
    }
    // Deliberately NOT reacting to `initialCompose` changes while the
    // dialog stays open — only the `open` transition re-seeds it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleClose() {
    setComposeText("");
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    mutation.reset();
    onClose();
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileError(null);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setComposeText(reader.result);
      }
    };
    reader.onerror = () => {
      setFileError(`Couldn't read "${file.name}". Paste the file's contents instead.`);
    };
    reader.readAsText(file);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate(composeText, {
      onSuccess: (result) => {
        pushToast({
          title: `Imported ${result.services.length} service${result.services.length === 1 ? "" : "s"}, ${result.connections.length} connection${result.connections.length === 1 ? "" : "s"}.`,
          tone: "success",
        });
        // Stays on the project page (unlike CreateServiceModal's single-service
        // navigate) — an import can create many services at once, so there is
        // no one obviously "the" service to jump to; the refreshed table below
        // is the result.
        handleClose();
      },
    });
  }

  const canSubmit = composeText.trim().length > 0 && !mutation.isPending;

  return (
    <Modal open={open} title="Import from compose" onClose={handleClose} initialFocusId={textareaId} size="lg">
      <form onSubmit={handleSubmit} noValidate>
        <p className="form-note">
          Every service and its <code>depends_on</code>/shared-network edges are created as-is — nothing is deployed
          or placed on a node yet. Requires an empty project (no services yet); importing into a project that
          already has services isn't supported.
        </p>

        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error.status === 409
              ? "This project already has services — import needs an empty project. Merging an import into an existing project isn't supported yet."
              : mutation.error.message || "Couldn't import this compose file."}
          </p>
        )}

        {fileError && (
          <p className="form-error" role="alert">
            {fileError}
          </p>
        )}

        <div className="form-field__file-row">
          <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()}>
            Choose a file…
          </Button>
          <span>or paste a compose.yml below</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_EXTENSIONS}
            onChange={handleFileChange}
            className="visually-hidden"
            aria-label="Compose file"
          />
        </div>

        <label className="form-field" htmlFor={textareaId}>
          <span>compose.yml</span>
          <textarea
            id={textareaId}
            spellCheck={false}
            rows={16}
            placeholder={"services:\n  web:\n    image: nginx:1.27\n"}
            value={composeText}
            onChange={(e) => setComposeText(e.target.value)}
            required
          />
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Importing…" : "Import"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
